/**
 * For You — the LEGACY page (interest rows: hybrid search per interest via
 * GET /api/v1/me/recommendations/v2, bookmarks via /api/v1/saved).
 *
 * Restored verbatim from git (0e36ef2^:components/ForYouPage.tsx) — only the
 * component's name changed — so members keep the page they had while the new
 * four-section page (./foryou) is behind Admin → For You & What's New →
 * Feature access. App.tsx renders this one whenever the server does not say
 * "foryou" is enabled for the account (utils/featureAccess.ts). None of its
 * endpoints are /api/v1/foryou/*, so the backend gate does not touch it.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MIN_INTERESTS_FOR_RECOMMENDATIONS } from '../constants';
import { InterestsData, SavedCardType, SavedItem } from '../types';
import { ForYouInterestTag } from './ForYouInterestTag';
import { trackClick } from '../utils/analytics';
import { ChevronUpIcon } from './icons/ChevronUpIcon';
import { ChevronDownIcon } from './icons/ChevronDownIcon';
import { SavedItemCard } from './SavedItemCard';
import { PencilIcon } from './icons/PencilIcon';
import { apiService } from '../services/apiService';
import { friendlyAuthError } from '../utils/authErrors';

interface ForYouPageLegacyProps {
    interestsData: InterestsData;
    setActiveView: (view: string) => void;
    onReadMore: (type: SavedCardType, id: string) => void;
    onNavigateToProfileTab?: (tab: string) => void;
}

interface RecommendationCategory {
    name: string;
    items: SavedItem[];
    isLoading?: boolean; // Track loading state per category
}


export const ForYouPageLegacy: React.FC<ForYouPageLegacyProps> = ({ interestsData, setActiveView, onReadMore, onNavigateToProfileTab }) => {
    const [bookmarkedItems, setBookmarkedItems] = useState<Set<string>>(new Set());
    const [isInterestsExpanded, setIsInterestsExpanded] = useState(true);
    const [recommendations, setRecommendations] = useState<RecommendationCategory[]>([]);
    const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(true); // Start with loading state
    const [recommendationsError, setRecommendationsError] = useState<string>('');
    const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false); // Track if we've attempted to load
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    // Track previous keywords to detect additions/removals
    const previousKeywordsRef = useRef<string[]>([]);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const cacheHydratedRef = useRef(false);

    const userId = apiService.getStoredUserId();
    const recommendationsCacheKey = userId ? `for_you_recommendations_v2:${userId}` : null;

    const loadRecommendationsCache = (): { categories: RecommendationCategory[], updatedAt: string | null } | null => {
        if (!recommendationsCacheKey) return null;
        try {
            const raw = sessionStorage.getItem(recommendationsCacheKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const categories = Array.isArray(parsed?.categories) ? parsed.categories : null;
            if (!categories) return null;
            const updatedAt = parsed?.updated_at || null;
            // Basic shape check
            const validCategories = categories
                .filter((c: any) => typeof c?.name === 'string' && Array.isArray(c?.items))
                .map((c: any) => ({ name: c.name, items: c.items as SavedItem[], isLoading: false }));
            return { categories: validCategories, updatedAt };
        } catch (e) {
            console.warn('Failed to load ForYou recommendations cache:', e);
            return null;
        }
    };

    const saveRecommendationsCache = (cats: RecommendationCategory[], updatedAt?: string | null) => {
        if (!recommendationsCacheKey) return;
        try {
            const payload = {
                updated_at: updatedAt || lastUpdated || new Date().toISOString(),
                categories: cats.map(c => ({
                    name: c.name,
                    items: c.items,
                })),
            };
            sessionStorage.setItem(recommendationsCacheKey, JSON.stringify(payload));
        } catch (e) {
            console.warn('Failed to save ForYou recommendations cache:', e);
        }
    };

    // Helper function to convert API results to SavedItem format
    const convertResultsToSavedItems = (results: any[]): SavedItem[] => {
        // Sort results within each category by relevance (highest to lowest)
        const sortedResults = results.sort((a: any, b: any) => {
            // Use AI relevance score if available, otherwise fallback to original score
            const scoreA = a.ai_relevance_score || a.percentage || a.score * 100 || 0;
            const scoreB = b.ai_relevance_score || b.percentage || b.score * 100 || 0;
            return scoreB - scoreA; // Highest to lowest
        });
        
        return sortedResults.map((result, index) => {
            const metadata = result.metadata || {};
            
            // Extract TRL information with improved parsing
            let trlDetail = 'TRL Not Disclosed';
            try {
                // First try to get from development_stage
                let devStage = metadata.development_stage;
                let trlValue = null;
                
                if (devStage) {
                    // Handle different formats of development_stage
                    if (typeof devStage === 'string') {
                        try {
                            devStage = JSON.parse(devStage);
                        } catch (parseError) {
                            // If it's not JSON but contains TRL info, try to extract
                            const match = devStage.match(/(\d+)/);
                            if (match) {
                                trlValue = parseInt(match[1]);
                            }
                        }
                    }
                    
                    // If parsed as object, get TRL from various field names
                    // Check for "Not Disclosed" case first
                    if (devStage && typeof devStage === 'object') {
                        const possibleTrl = devStage.calculated_trl || 
                                          devStage.original_trl || 
                                          devStage.trl || 
                                          devStage.TRL;
                        
                        if (possibleTrl !== undefined && possibleTrl !== null && possibleTrl !== '') {
                            const trlString = String(possibleTrl).toLowerCase();
                            // Check for "Not Disclosed" explicitly
                            if (trlString.includes('not disclosed') || trlString.includes('not available')) {
                                trlValue = -1; // Marker for "Not Disclosed"
                            } else {
                                // Check confidence score
                                const confidenceScore = devStage.confidence_score;
                                if (confidenceScore !== undefined && confidenceScore !== null) {
                                    const confValue = parseInt(String(confidenceScore));
                                    if (confValue < 3) {
                                        trlValue = -1; // Low confidence, treat as not disclosed
                                    } else {
                                        // Extract digits from TRL value using regex
                                        const match = trlString.match(/(\d+)/);
                                        if (match) {
                                            trlValue = parseInt(match[1]);
                                        }
                                    }
                                } else {
                                    // No confidence score, extract TRL
                                    const match = trlString.match(/(\d+)/);
                                    if (match) {
                                        trlValue = parseInt(match[1]);
                                    }
                                }
                            }
                        }
                    }
                }
                
                // Try calculated_trl_statement field
                if (!trlValue && metadata.calculated_trl_statement) {
                    const match = metadata.calculated_trl_statement.match(/trl\s*(\d+)/i);
                    if (match) {
                        trlValue = parseInt(match[1]);
                    }
                }
                
                // Fallback: search in title and summary
                if (!trlValue) {
                    const title = metadata.utech_title || metadata.title || '';
                    const summary = metadata.utech_summary || '';
                    const combinedText = `${title} ${summary}`.toLowerCase();
                    
                    const trlMatch = combinedText.match(/trl\s*(\d+)/i);
                    if (trlMatch) {
                        trlValue = parseInt(trlMatch[1]);
                    }
                }
                
                // Set final TRL detail
                if (trlValue === -1) {
                    // Explicit "Not Disclosed" marker
                    trlDetail = 'TRL Not Disclosed';
                } else if (trlValue && trlValue >= 1 && trlValue <= 9) {
                    trlDetail = `TRL ${trlValue}`;
                } else if (trlValue && trlValue > 0) {
                    trlDetail = `TRL ${trlValue}`;
                } else {
                    trlDetail = 'TRL Not Disclosed';
                }
                
            } catch (e) {
                console.warn(`TRL extraction failed for ${metadata.utech_technology_id}:`, e);
                trlDetail = 'TRL Not Disclosed';
            }
            
            // Keep relevance text simple for user display
            const relevanceText = 'Recommended Tech';
            
            return {
                id: metadata.utech_technology_id || `rec_${index}`,
                type: SavedCardType.Tech,
                relevance: relevanceText,
                title: metadata.utech_title || metadata.title || 'Unknown Technology',
                location: [metadata.university_name, metadata.university_country].filter(Boolean).join(', ') || 'Unknown',
                detail: trlDetail,
                verified: true, // Assume verified for recommendations
                showStatusIcon: true
            };
        });
    };

    // Persist recommendations locally so navigation/remount doesn't force reload on keyword removal
    useEffect(() => {
        if (!recommendationsCacheKey) return;
        if (!cacheHydratedRef.current) return; // don't overwrite cache before hydration
        // Only cache fully-loaded categories
        const cachable = recommendations.filter(c => !c.isLoading);
        saveRecommendationsCache(cachable);
    }, [recommendations, recommendationsCacheKey]);

    // Initial load: Load all recommendations when component mounts
    useEffect(() => {
        const loadRecommendations = async () => {
            if (!userId) {
                setIsLoadingRecommendations(false);
                setHasAttemptedLoad(true);
                previousKeywordsRef.current = [...interestsData.keywords];
                return;
            }
            
            // Check if user has enough interests for recommendations
            if (interestsData.keywords.length < MIN_INTERESTS_FOR_RECOMMENDATIONS) {
                setRecommendations([]);
                setRecommendationsError('');
                setIsLoadingRecommendations(false);
                setHasAttemptedLoad(true);
                previousKeywordsRef.current = [...interestsData.keywords];
                return;
            }

            // Hydrate from cache first (prevents reloading all categories after keyword removals + navigation)
            const cacheResult = loadRecommendationsCache();
            if (cacheResult && cacheResult.categories.length > 0) {
                if (cacheResult.updatedAt) setLastUpdated(cacheResult.updatedAt);
                // Only show categories that still exist in current keywords
                const currentKeywords = interestsData.keywords;
                const currentSet = new Set(currentKeywords);
                const filteredCached = cacheResult.categories.filter(c => currentSet.has(c.name));

                // If cache doesn't include newly-added keywords, show loaders for missing keywords
                const cachedNames = new Set(filteredCached.map(c => c.name));
                const missingKeywords = currentKeywords.filter(k => !cachedNames.has(k));

                // Build categories in keyword order (cached first, then loaders)
                const categoriesByName = new Map(filteredCached.map(c => [c.name, c]));
                const hydratedCategories: RecommendationCategory[] = currentKeywords.map(k => {
                    const existing = categoriesByName.get(k);
                    if (existing) return existing;
                    return { name: k, items: [], isLoading: true };
                });

                setRecommendations(hydratedCategories);
                setIsLoadingRecommendations(false);
                setRecommendationsError('');
                setHasAttemptedLoad(true);
                cacheHydratedRef.current = true;

                // If there are missing keywords, trigger backend fetch for ONLY those keywords (debounced)
                if (missingKeywords.length > 0) {
                    // Clear existing debounce timer
                    if (debounceTimerRef.current) {
                        clearTimeout(debounceTimerRef.current);
                        debounceTimerRef.current = null;
                    }

                    debounceTimerRef.current = setTimeout(async () => {
                        console.log(`⏱️ Cache hydrate: fetching recommendations for new/missing keywords: ${missingKeywords.join(', ')}`);
                        try {
                            const response = await apiService.getPersonalizedRecommendationsV2(userId);
                            if (response.success && response.data?.categories) {
                                const aiCategorizedResults = response.data.categories;

                                setRecommendations(prev => {
                                    const map = new Map(prev.map(cat => [cat.name, { ...cat }]));

                                    missingKeywords.forEach(tag => {
                                        const categoryData = aiCategorizedResults[tag];
                                        if (categoryData) {
                                            const results = (categoryData as any).results || [];
                                            const newItems = convertResultsToSavedItems(results);
                                            map.set(tag, { name: tag, items: newItems.slice(0, 10), isLoading: false });
                                        } else {
                                            const existing = map.get(tag);
                                            map.set(tag, { name: tag, items: existing?.items || [], isLoading: false });
                                        }
                                    });

                                    return Array.from(map.values());
                                });
                            } else {
                                setRecommendations(prev => prev.map(cat => (
                                    missingKeywords.includes(cat.name) ? { ...cat, isLoading: false } : cat
                                )));
                            }
                        } catch (e) {
                            setRecommendations(prev => prev.map(cat => (
                                missingKeywords.includes(cat.name) ? { ...cat, isLoading: false } : cat
                            )));
                        }
                    }, 3000);
                }

                // Treat current keywords as the baseline from now on
                previousKeywordsRef.current = [...currentKeywords];
                return;
            }
            
            // Immediately show category-wise loaders for all keywords
            setRecommendations(interestsData.keywords.map(keyword => ({
                name: keyword,
                items: [],
                isLoading: true
            })));
            setIsLoadingRecommendations(false); // Don't show global loader
            setRecommendationsError('');
            setHasAttemptedLoad(true);
            
            try {
                const response = await apiService.getPersonalizedRecommendationsV2(userId);
                
                if (response.success && response.data?.categories) {
                    // V2: Results are organized by keyword with direct mapping
                    const aiCategorizedResults = response.data.categories;
                    
                    console.log(`✅ Received V2 recommendations with ${response.data.total_categories} categories`);
                    console.log(`⏱️ Processing time: ${response.data.processing_time}s (Enhancement: ${response.data.enhancement_time}s, Search: ${response.data.search_time}s, Scoring: ${response.data.scoring_time}s)`);
                    
                    // Convert search results to SavedItem format for the UI
                    const recommendationCategories: RecommendationCategory[] = Object.entries(aiCategorizedResults).map(([categoryName, categoryData]) => {
                        // V2: categoryData has structure { results: [...], count: N, enhanced_query: "..." }
                        const results = (categoryData as any).results || [];
                        const items = convertResultsToSavedItems(results);
                        
                        return {
                            name: categoryName,
                            items: items.slice(0, 10), // Limit to 10 items per category
                            isLoading: false
                        };
                    });
                    
                    setRecommendations(recommendationCategories); // Show all available categories
                    // Use cache_created from API if available (cached response), otherwise current time (fresh response)
                    setLastUpdated((response.data as any)?.cache_created || new Date().toISOString());
                    console.log(`✅ Loaded ${recommendationCategories.length} recommendation categories`);
                    
                } else {
                    setRecommendationsError(friendlyAuthError(response.error, 'Failed to load recommendations.'));
                    console.error('Failed to load recommendations:', response.error);
                    // Remove loading state from all categories on error
                    setRecommendations(prev => prev.map(cat => ({ ...cat, isLoading: false })));
                }
                
            } catch (error) {
                setRecommendationsError('Network error loading recommendations');
                console.error('Error loading recommendations:', error);
                // Remove loading state from all categories on error
                setRecommendations(prev => prev.map(cat => ({ ...cat, isLoading: false })));
            } finally {
                previousKeywordsRef.current = [...interestsData.keywords];
                cacheHydratedRef.current = true;
            }
        };
        
        // Only load on initial mount
        if (previousKeywordsRef.current.length === 0) {
            loadRecommendations();
        } else {
            previousKeywordsRef.current = [...interestsData.keywords];
        }
    }, []); // Empty dependency array - only run on mount

    // Handle tag additions/removals
    useEffect(() => {
        const userId = apiService.getStoredUserId();
        if (!userId || previousKeywordsRef.current.length === 0) {
            // Skip if initial load hasn't happened yet
            return;
        }

        const currentKeywords = interestsData.keywords;
        const previousKeywords = previousKeywordsRef.current;

        // Skip if keywords haven't changed
        if (currentKeywords.length === previousKeywords.length && 
            currentKeywords.every((kw, idx) => kw === previousKeywords[idx])) {
            return;
        }

        // Detect added tags (tags in current but not in previous)
        const addedTags = currentKeywords.filter(tag => !previousKeywords.includes(tag));
        // Detect removed tags (tags in previous but not in current)
        const removedTags = previousKeywords.filter(tag => !currentKeywords.includes(tag));

        // Handle tag removal: Remove the category from recommendations (don't trigger API call)
        if (removedTags.length > 0) {
            console.log(`🗑️ Tags removed: ${removedTags.join(', ')} - Removing from recommendations without API call`);
            
            // Clear any pending debounce timer to prevent accidental triggers
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            
            // Remove categories from recommendations
            setRecommendations(prev => prev.filter(cat => !removedTags.includes(cat.name)));
            // Persist removal immediately (prevents other categories from "refreshing" after navigation)
            if (recommendationsCacheKey) {
                try {
                    const cacheResult = loadRecommendationsCache();
                    const updated = (cacheResult?.categories || []).filter(c => !removedTags.includes(c.name));
                    saveRecommendationsCache(updated);
                } catch {
                    // ignore cache errors
                }
            }
            
            // Update ref immediately to prevent re-triggering
            previousKeywordsRef.current = [...currentKeywords];
            
            // Early return - don't process additions if there were removals
            // This ensures we only handle removals, not additions, in this cycle
            return;
        }

        // Handle tag addition: Add loading row immediately, then fetch after debounce
        if (addedTags.length > 0) {
            console.log(`➕ Tags added: ${addedTags.join(', ')} - Adding loading rows and scheduling fetch`);
            
            // Immediately add loading rows for new tags
            setRecommendations(prev => {
                const existingCategoryNames = new Set(prev.map(cat => cat.name));
                const newCategories: RecommendationCategory[] = addedTags
                    .filter(tag => !existingCategoryNames.has(tag))
                    .map(tag => ({
                        name: tag,
                        items: [],
                        isLoading: true
                    }));
                
                return [...prev, ...newCategories];
            });

            // Clear existing debounce timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Debounce: Wait 3 seconds before fetching recommendations
            debounceTimerRef.current = setTimeout(async () => {
                console.log(`⏱️ Debounce complete - Fetching recommendations for new tags: ${addedTags.join(', ')}`);
                
                try {
                    // Fetch all recommendations (API generates for all, but we'll only update new tags)
                    const response = await apiService.getPersonalizedRecommendationsV2(userId);
                    
                    if (response.success && response.data?.categories) {
                        const aiCategorizedResults = response.data.categories;
                        
                        console.log(`✅ Received V2 recommendations with ${response.data.total_categories} categories`);
                        
                        // Update recommendations: merge new tag results with existing ones
                        // Keep existing categories unchanged, only update new tags
                        setRecommendations(prev => {
                            const existingCategoriesMap = new Map(
                                prev.map(cat => [cat.name, { ...cat }]) // Clone existing categories
                            );
                            
                            // Update only the new tags with fresh results
                            addedTags.forEach(tag => {
                                const categoryData = aiCategorizedResults[tag];
                                if (categoryData) {
                                    const results = (categoryData as any).results || [];
                                    const newItems = convertResultsToSavedItems(results);
                                    
                                    // Get existing items for this category (if any)
                                    const existingCategory = existingCategoriesMap.get(tag);
                                    const existingItems = existingCategory?.items || [];
                                    
                                    // Create a Set of item IDs to avoid duplicates
                                    const existingItemIds = new Set(existingItems.map(item => item.id));
                                    
                                    // Filter out duplicates from new items
                                    const uniqueNewItems = newItems.filter(item => !existingItemIds.has(item.id));
                                    
                                    // Merge: prioritize new items, then add old ones up to 10 total
                                    const newItemsToAdd = uniqueNewItems.slice(0, 10);
                                    const remainingSlots = 10 - newItemsToAdd.length;
                                    const oldItemsToAdd = remainingSlots > 0 
                                        ? existingItems.slice(0, remainingSlots)
                                        : [];
                                    
                                    const mergedItems = [
                                        ...newItemsToAdd, // New items first (prioritized)
                                        ...oldItemsToAdd // Fill remaining slots with old items
                                    ]; // Already limited to 10 total
                                    
                                    existingCategoriesMap.set(tag, {
                                        name: tag,
                                        items: mergedItems,
                                        isLoading: false
                                    });
                                    
                                    console.log(`✅ Updated ${tag}: ${uniqueNewItems.length} new items, ${existingItems.length} existing items, ${mergedItems.length} total`);
                                } else {
                                    // Tag not found in results, remove loading state but keep existing items if any
                                    const existingCategory = existingCategoriesMap.get(tag);
                                    existingCategoriesMap.set(tag, {
                                        name: tag,
                                        items: existingCategory?.items || [],
                                        isLoading: false
                                    });
                                }
                            });
                            
                            return Array.from(existingCategoriesMap.values());
                        });
                        
                        console.log(`✅ Updated recommendations for new tags: ${addedTags.join(', ')}`);
                    } else {
                        // On error, remove loading state for new tags but keep existing items
                        setRecommendations(prev => prev.map(cat => {
                            if (addedTags.includes(cat.name)) {
                                return { ...cat, isLoading: false };
                            }
                            return cat;
                        }));
                        console.error('Failed to load recommendations for new tags:', response.error);
                    }
                } catch (error) {
                    // On error, remove loading state for new tags but keep existing items
                    setRecommendations(prev => prev.map(cat => {
                        if (addedTags.includes(cat.name)) {
                            return { ...cat, isLoading: false };
                        }
                        return cat;
                    }));
                    console.error('Error loading recommendations for new tags:', error);
                }
                
                previousKeywordsRef.current = [...currentKeywords];
            }, 3000); // 3 second debounce
        } else {
            // No changes, just update the ref
            previousKeywordsRef.current = [...currentKeywords];
        }

        // Cleanup function
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [interestsData.keywords]); // Watch for keyword changes

    // Load bookmark states for all recommendation items
    useEffect(() => {
        const loadBookmarkStates = async () => {
            const userId = apiService.getStoredUserId();
            if (!userId || recommendations.length === 0) return;

            try {
                // Get all technology IDs from recommendations
                const allTechIds: string[] = [];
                recommendations.forEach(category => {
                    category.items.forEach(item => {
                        if (item.type === SavedCardType.Tech) {
                            allTechIds.push(item.id);
                        }
                    });
                });

                // Load saved states for all technologies
                const response = await apiService.getSavedCards(userId);
                if (response.success && response.data?.items) {
                    const savedItems = response.data.items;
                    const bookmarkedTechIds = new Set<string>();
                    
                    savedItems.forEach((savedItem: any) => {
                        if (savedItem.asset_type === 'technology' && savedItem.bookmarked) {
                            bookmarkedTechIds.add(savedItem.asset_id);
                        }
                    });
                    
                    setBookmarkedItems(bookmarkedTechIds);
                    console.log(`✅ Loaded bookmark states for ${bookmarkedTechIds.size} technologies`);
                }
            } catch (error) {
                console.error('Failed to load bookmark states:', error);
            }
        };

        loadBookmarkStates();
    }, [recommendations]); // Reload when recommendations change

    const handleBookmarkToggle = async (id: string) => {
        trackClick('feed_click_bookmark', { id });
        const userId = apiService.getStoredUserId();
        if (!userId) return;

        try {
            const isCurrentlyBookmarked = bookmarkedItems.has(id);
            const newBookmarkedState = !isCurrentlyBookmarked;

            console.log(`🔖 Toggling bookmark for ${id}: ${isCurrentlyBookmarked} → ${newBookmarkedState}`);

            // Call the bookmark API
            const response = await apiService.saveCard({
                asset_type: 'technology',
                asset_id: id,
                bookmarked: newBookmarkedState,
                liked: null,
                linked_chat_id: null
            }, userId);

            if (response.success) {
                // Update local state
                setBookmarkedItems(prev => {
                    const newSet = new Set(prev);
                    if (newBookmarkedState) {
                        newSet.add(id);
                    } else {
                        newSet.delete(id);
                    }
                    return newSet;
                });
                console.log(`✅ Bookmark updated for ${id}`);
            } else {
                console.error('Failed to update bookmark:', response.error);
            }
        } catch (error) {
            console.error('Error toggling bookmark:', error);
        }
    };

    // Manual refresh: re-fetch all keyword recommendations from scratch
    const handleRefreshAll = async () => {
        trackClick('feed_click_refresh');
        if (!userId || isRefreshing || interestsData.keywords.length < MIN_INTERESTS_FOR_RECOMMENDATIONS) return;
        
        setIsRefreshing(true);
        
        // Clear frontend session cache
        if (recommendationsCacheKey) {
            sessionStorage.removeItem(recommendationsCacheKey);
        }
        
        // Set all categories to loading
        setRecommendations(interestsData.keywords.map(keyword => ({
            name: keyword,
            items: [],
            isLoading: true
        })));
        setRecommendationsError('');
        
        try {
            // Call with force_refresh=true to bypass backend cache
            const response = await apiService.getPersonalizedRecommendationsV2(userId, true);
            
            if (response.success && response.data?.categories) {
                const aiCategorizedResults = response.data.categories;
                const recommendationCategories: RecommendationCategory[] = Object.entries(aiCategorizedResults).map(([categoryName, categoryData]) => {
                    const results = (categoryData as any).results || [];
                    const items = convertResultsToSavedItems(results);
                    return {
                        name: categoryName,
                        items: items.slice(0, 10),
                        isLoading: false
                    };
                });
                
                setRecommendations(recommendationCategories);
                setLastUpdated(new Date().toISOString());
                console.log(`✅ Refreshed ${recommendationCategories.length} recommendation categories`);
            } else {
                setRecommendationsError(friendlyAuthError(response.error, 'Failed to refresh recommendations.'));
                setRecommendations(prev => prev.map(cat => ({ ...cat, isLoading: false })));
            }
        } catch (error) {
            setRecommendationsError('Network error refreshing recommendations');
            console.error('Error refreshing recommendations:', error);
            setRecommendations(prev => prev.map(cat => ({ ...cat, isLoading: false })));
        } finally {
            setIsRefreshing(false);
        }
    };

    // Format last updated time for display
    const formatLastUpdated = (isoString: string | null): string => {
        if (!isoString) return '';
        try {
            const date = new Date(isoString);
            const now = new Date();
            const diffMs = now.getTime() - date.getTime();
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMins / 60);
            const diffDays = Math.floor(diffHours / 24);
            
            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins}m ago`;
            if (diffHours < 24) return `${diffHours}h ago`;
            if (diffDays < 7) return `${diffDays}d ago`;
            return date.toLocaleDateString();
        } catch {
            return '';
        }
    };

    // Sort recommendations to match the order of keywords
    const sortedRecommendations = useMemo(() => {
        if (recommendations.length === 0) return [];
        
        // Create a map for quick lookup
        const recommendationsMap = new Map(recommendations.map(rec => [rec.name, rec]));
        
        // Sort recommendations based on keyword order
        const sorted: RecommendationCategory[] = [];
        
        // First, add categories in the order of keywords
        interestsData.keywords.forEach(keyword => {
            const category = recommendationsMap.get(keyword);
            if (category) {
                sorted.push(category);
                recommendationsMap.delete(keyword); // Remove from map after adding
            }
        });
        
        // Then, add any remaining categories that weren't in keywords (shouldn't happen, but just in case)
        recommendationsMap.forEach(category => {
            sorted.push(category);
        });
        
        // Reverse so latest-added keyword appears first
        sorted.reverse();
        
        return sorted;
    }, [recommendations, interestsData.keywords]);

    return (
        <main className="flex flex-col flex-1 self-stretch rounded-2xl bg-white shadow-custom-light p-4 sm:p-6 gap-6 overflow-y-auto hide-scrollbar">
            <div className="p-4 sm:p-6 flex-col justify-center items-end gap-6 self-stretch rounded-2xl border border-grayscale-300">
                <div className="flex flex-col items-start gap-6 self-stretch">
                    <div className="flex justify-between items-start self-stretch">
                        <h3 className="text-grayscale-1200 font-grotesk text-base font-bold pr-4">
                            {interestsData.keywords.length >= MIN_INTERESTS_FOR_RECOMMENDATIONS 
                                ? "Based on your chosen interests, profile and past activity, here are technologies that could be useful for you!" 
                                : "Complete your profile to see personalized recommendations!"
                            }
                        </h3>
                        <div className="flex justify-end items-center gap-3 flex-shrink-0">
                            <button onClick={() => {
                                trackClick('feed_click_edit');
                                if (onNavigateToProfileTab) {
                                    onNavigateToProfileTab('interests');
                                } else {
                                    setActiveView('profile');
                                }
                            }} className="flex w-6 h-6 justify-center items-center"><PencilIcon /></button>
                            <button onClick={() => { trackClick('feed_click_collapse_expand'); setIsInterestsExpanded(prev => !prev); }} className="flex w-6 h-6 justify-center items-center">
                                {isInterestsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon className="w-6 h-6"/>}
                            </button>
                        </div>
                    </div>
                    {isInterestsExpanded && (
                        <div className="flex items-start content-start gap-3 self-stretch flex-wrap">
                            {[...interestsData.keywords].reverse().map(keyword => (
                                <ForYouInterestTag key={keyword} text={keyword} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Refresh button and last updated */}
            {interestsData.keywords.length >= MIN_INTERESTS_FOR_RECOMMENDATIONS && hasAttemptedLoad && (
                <div className="flex justify-between items-center self-stretch">
                    <span className="text-grayscale-500 font-grotesk text-xs">
                        {lastUpdated ? `Last updated: ${formatLastUpdated(lastUpdated)}` : ''}
                    </span>
                    <button
                        onClick={handleRefreshAll}
                        disabled={isRefreshing || isLoadingRecommendations}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-grayscale-300 text-grayscale-800 font-grotesk text-xs font-medium hover:bg-grayscale-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.65 2.35A8 8 0 1 0 16 8h-2a6 6 0 1 1-1.76-4.24L10 6h6V0l-2.35 2.35z" fill="currentColor"/>
                        </svg>
                        {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
                    </button>
                </div>
            )}

            {/* Show different content based on interests and recommendations */}
            {interestsData.keywords.length < MIN_INTERESTS_FOR_RECOMMENDATIONS ? (
                // Show "complete profile" message when insufficient interests
                <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                        <PencilIcon />
                    </div>
                    <div className="flex flex-col gap-2">
                        <h4 className="text-grayscale-1200 font-grotesk text-lg font-bold">Complete Your Profile</h4>
                        <p className="text-grayscale-700 font-grotesk text-sm max-w-md">
                            Add at least one interest to your profile to start seeing personalized technology recommendations from leading universities.
                        </p>
                        <button 
                            onClick={() => {
                                trackClick('complete_profile_click_cta', { source: 'for_you_empty_state' });
                                setActiveView('profile');
                            }}
                            className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg font-grotesk text-sm font-medium hover:bg-primary-600 transition-colors"
                        >
                            Complete Profile
                        </button>
                    </div>
                </div>
            ) : isLoadingRecommendations ? (
                // Show loading state
                <div className="flex flex-col items-center justify-center gap-4 p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
                    <p className="text-grayscale-700 font-grotesk text-sm">
                        Generating personalized recommendations based on your interests...
                    </p>
                </div>
            ) : recommendationsError ? (
                // Show error state
                <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
                    <div className="w-16 h-16 bg-red-50 border border-red-200 rounded-full flex items-center justify-center">
                        <span className="text-2xl">⚠️</span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <h4 className="text-grayscale-1200 font-grotesk text-lg font-bold">Unable to Load Recommendations</h4>
                        <p className="text-grayscale-700 font-grotesk text-sm">{recommendationsError}</p>
                    </div>
                </div>
            ) : isLoadingRecommendations ? (
                // Show loading state when recommendations are being loaded
                <div className="flex flex-col items-center justify-center gap-4 p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
                    <p className="text-grayscale-700 font-grotesk text-sm">
                        Loading your personalized recommendations...
                    </p>
                </div>
            ) : recommendations.length === 0 && hasAttemptedLoad ? (
                // Show no recommendations state only after we've attempted to load
                <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
                    <div className="w-16 h-16 bg-grayscale-100 rounded-full flex items-center justify-center">
                        <span className="text-grayscale-500 text-2xl">🔍</span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <h4 className="text-grayscale-1200 font-grotesk text-lg font-bold">No Recommendations Found</h4>
                        <p className="text-grayscale-700 font-grotesk text-sm">
                            We couldn't find relevant technologies based on your current interests. Try updating your interests or profile.
                        </p>
                    </div>
                </div>
            ) : (
                // Show personalized recommendations by category (sorted by keyword order)
            <div className="flex flex-col items-start gap-4 self-stretch pb-8">
                    {sortedRecommendations.map((category) => (
                        <div key={category.name} className="flex flex-col items-start gap-4 self-stretch">
                            <h4 className="text-grayscale-1200 font-grotesk text-base font-bold">
                                {category.name} ({category.isLoading ? '...' : category.items.length})
                            </h4>
                        {category.isLoading ? (
                            // Show loading state for this category
                            <div className="flex flex-col items-center justify-center gap-4 p-8 w-full">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
                                <p className="text-grayscale-700 font-grotesk text-sm">
                                    Loading recommendations for {category.name}...
                                </p>
                            </div>
                        ) : category.items.length === 0 ? (
                            // Show empty state for this category
                            <div className="flex flex-col items-center justify-center gap-2 p-4 w-full">
                                <p className="text-grayscale-500 font-grotesk text-sm">
                                    No recommendations found for {category.name}
                                </p>
                            </div>
                        ) : (
                            // Show items for this category
                        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-3 self-stretch sm:overflow-x-auto custom-scrollbar pb-4 pt-2">
                                {category.items.map(item => (
                                <div key={item.id} className="w-full sm:w-auto flex-shrink-0">
                                    <SavedItemCard 
                                        item={item} 
                                            isBookmarked={bookmarkedItems.has(item.id)}
                                        onBookmarkToggle={handleBookmarkToggle}
                                        onReadMore={() => { trackClick('feed_click_read_more', { title: item.title, id: item.id }); onReadMore(item.type, item.id); }}
                                    />
                                </div>
                            ))}
                        </div>
                        )}
                    </div>
                ))}
            </div>
            )}

        </main>
    );
};

export default ForYouPageLegacy;
